// Shared notifications API (SeriesAI/in-house implementation: input normalisation and
// empty-id guard; LitigAI and Atticus were strict subsets). GraphQL goes through the
// host client via @juristai/shared djangoGraphql.
import { executeDjangoGraphQl } from "./djangoGraphql";

const unwrap = (response) => response?.data?.data || {};
const normalizeOptionalString = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized || null;
};
const normalizeLimit = (value, fallback = 50) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback;
};
const FIELDS = `
  notificationId userId firmId appId caseId category eventType groupKey sourceId
  title summary destination createdAt readAt metadata
`;

/**
 * `byCase` / `byCategory` are declared as GraphQL JSONString, so they arrive as
 * serialised strings rather than objects. Parse defensively — a malformed
 * payload must not take the dashboard down.
 */
const parseCountMap = (value) => {
  if (!value) {
    return {};
  }
  if (typeof value === "object") {
    return value;
  }
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    console.error("Failed to parse notification count map:", error);
    return {};
  }
};

const normalizeSummary = (summary) =>
  summary
    ? {
        ...summary,
        byCase: parseCountMap(summary.byCase),
        byCategory: parseCountMap(summary.byCategory),
      }
    : null;

export const getNotificationSummary = ({ firmId, appId, caseId } = {}) =>
  executeDjangoGraphQl({
    operationName: "NotificationSummary",
    query: `query NotificationSummary($firmId: String, $appId: String, $caseId: String) {
      notificationSummary(firmId: $firmId, appId: $appId, caseId: $caseId) { firmCount caseCount totalCount byCase byCategory }
    }`,
    variables: {
      firmId: normalizeOptionalString(firmId),
      appId: normalizeOptionalString(appId),
      caseId: normalizeOptionalString(caseId),
    },
  }).then((response) => ({
    ...response,
    data: normalizeSummary(unwrap(response).notificationSummary),
  }));

export const getNotifications = ({ firmId, appId, caseId, category, unreadOnly = false, limit = 50 } = {}) =>
  executeDjangoGraphQl({
    operationName: "Notifications",
    query: `query Notifications($firmId: String, $appId: String, $caseId: String, $category: String, $unreadOnly: Boolean, $limit: Int) {
      notifications(firmId: $firmId, appId: $appId, caseId: $caseId, category: $category, unreadOnly: $unreadOnly, limit: $limit) { ${FIELDS} }
    }`,
    variables: {
      firmId: normalizeOptionalString(firmId),
      appId: normalizeOptionalString(appId),
      caseId: normalizeOptionalString(caseId),
      category: normalizeOptionalString(category),
      unreadOnly,
      limit: normalizeLimit(limit),
    },
  }).then((response) => ({ ...response, data: unwrap(response).notifications || [] }));

/**
 * Mark exactly one notification read. Preferred over the bulk mutation: it lets
 * the UI mark only what the user actually opened, expanded, or dismissed.
 */
export const markNotificationRead = ({ notificationId } = {}) => {
  const normalizedNotificationId = normalizeOptionalString(notificationId);
  if (!normalizedNotificationId) {
    return Promise.resolve({
      success: false,
      data: null,
      error: "Notification ID is required.",
    });
  }

  return executeDjangoGraphQl({
    operationName: "MarkNotificationRead",
    query: `mutation MarkNotificationRead($notificationId: String!) {
      markNotificationRead(notificationId: $notificationId) {
        notification { notificationId readAt }
      }
    }`,
    variables: { notificationId: normalizedNotificationId },
  }).then((response) => ({
    ...response,
    data: unwrap(response).markNotificationRead?.notification || null,
  }));
};

export const markNotificationsRead = ({ caseId, category } = {}) =>
  executeDjangoGraphQl({
    operationName: "MarkNotificationsRead",
    query: `mutation MarkNotificationsRead($caseId: String, $category: String) {
      markNotificationsRead(caseId: $caseId, category: $category) { count }
    }`,
    variables: {
      caseId: normalizeOptionalString(caseId),
      category: normalizeOptionalString(category),
    },
  }).then((response) => ({ ...response, data: unwrap(response).markNotificationsRead || null }));
